import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Paper,
  Grid,
  CircularProgress,
} from '@mui/material';
import { seedEvents, clearEvents } from '../../services/calendar.service';
import { toastService } from '../../services/toast.service';
import { useAppSelector } from '../../store';
import { selectUser } from '../../store/auth/authSlice';
import { format } from 'date-fns';

export const DevEventGenerator: React.FC = () => {
  const user = useAppSelector(selectUser);
  const [userId, setUserId] = useState<string>('');
  const [date, setDate] = useState<string>(format(new Date(), 'yyyy-MM-dd'));
  const [count, setCount] = useState<number>(20);
  const [loading, setLoading] = useState(false);
  const [clearing, setClearing] = useState(false);

  useEffect(() => {
    if (user) {
      setUserId(String(user.id));
    }
  }, [user]);

  const validateInputs = () => {
    const userIdNum = parseInt(userId, 10);
    if (!userId || isNaN(userIdNum)) {
      toastService.error('Будь ласка, введіть коректний ID користувача');
      return null;
    }
    if (!date) {
      toastService.error('Будь ласка, виберіть дату');
      return null;
    }
    return userIdNum;
  };

  const handleGenerate = async () => {
    const userIdNum = validateInputs();
    if (userIdNum === null) return;

    setLoading(true);
    try {
      await seedEvents({
        userId: userIdNum,
        date,
        count,
      });
      toastService.success(`Успішно згенеровано ${count} подій для користувача ${userIdNum} на ${date}`);
    } catch (error: any) {
      console.error('Failed to seed events', error);
      toastService.error(error.message || 'Не вдалося згенерувати події');
    } finally {
      setLoading(false);
    }
  };

  const handleClear = async () => {
    const userIdNum = validateInputs();
    if (userIdNum === null) return;

    if (!window.confirm(`Ви впевнені, що хочете видалити ВСІ події для користувача ${userIdNum} на дату ${date}?`)) {
      return;
    }

    setClearing(true);
    try {
      await clearEvents({
        userId: userIdNum,
        date,
      });
      toastService.success(`Події успішно видалено для користувача ${userIdNum} на ${date}`);
    } catch (error: any) {
      console.error('Failed to clear events', error);
      toastService.error(error.message || 'Не вдалося видалити події');
    } finally {
      setClearing(false);
    }
  };

  return (
    <Box>
      <Typography variant="h6" gutterBottom>Генератор тестових подій</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Цей інструмент дозволяє швидко наповнити календар користувача випадковими подіями (їжа, тренування, ліки тощо) для тестування відображення та пуш-сповіщень.
      </Typography>

      <Paper variant="outlined" sx={{ p: 3, maxWidth: 600 }}>
        <Grid container spacing={3}>
          <Grid item xs={12} sm={6}>
            <TextField
              label="User ID"
              value={userId}
              onChange={(e) => setUserId(e.target.value)}
              type="number"
              fullWidth
              helperText="ID користувача, якому створюємо/видаляємо події"
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Дата"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              type="date"
              fullWidth
              InputLabelProps={{ shrink: true }}
              helperText="Дата для генерації або очищення подій"
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Кількість подій"
              value={count}
              onChange={(e) => setCount(parseInt(e.target.value, 10))}
              type="number"
              fullWidth
              inputProps={{ min: 1, max: 100 }}
              helperText="Скільки подій згенерувати (макс. 100)"
            />
          </Grid>
          <Grid item xs={12} sx={{ display: 'flex', gap: 2 }}>
            <Button
              variant="contained"
              color="primary"
              onClick={handleGenerate}
              disabled={loading || clearing}
              startIcon={loading ? <CircularProgress size={20} color="inherit" /> : null}
            >
              {loading ? 'Генеруємо...' : 'Згенерувати на дату'}
            </Button>
            
            <Button
              variant="outlined"
              color="error"
              onClick={handleClear}
              disabled={loading || clearing}
              startIcon={clearing ? <CircularProgress size={20} color="inherit" /> : null}
            >
              {clearing ? 'Видаляємо...' : 'Очистити на дату'}
            </Button>
          </Grid>
        </Grid>
      </Paper>
    </Box>
  );
};
